import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";

import { Box, Stack, Divider, Typography, Button, Pagination, Tooltip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  DataGrid,
  GridColDef,
  useGridApiRef,
  useGridSelector,
  gridPageSelector,
  gridPageCountSelector,
  GridRowSelectionModel,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";

import { HttpChannelApi } from "../../interface/channel-api";
import { HttpAdminApi } from "../../interface/admin-api";

import { userState, broadStatusName } from "../../interface/MainInterface";
import { CommonUtils } from "../../utils/common_utils";
import LoadingCircle from "../../utils/LoadingCircle";
import Toast from "../../utils/Toast";

import { useWindowSize } from "../../utils/useWindowSize";

interface propsType {
  userState: userState;
}

const channelApi = new HttpChannelApi();
const adminApi = new HttpAdminApi();
const cUtils = new CommonUtils();

const pageSize: number = 100;
// 방송별 통계는 조회 건수에 비례하여 느려지므로 조회 기간에 상한을 둔다.
const maxSearchDays: number = 365;

const TotalStatistic = (props: propsType) => {
  const navigate = useNavigate();
  const windowSize = useWindowSize();

  const toastRef: any = useRef();
  const gridApi = useGridApiRef();
  const [isLoading, setIsLoading] = useState(false);

  const [srchDateFrom, setSrchDateFrom] = useState<Dayjs | null>(dayjs().startOf("month"));
  const [srchDateTo, setSrchDateTo] = useState<Dayjs | null>(dayjs().endOf("month"));

  const [channelList, setChannelList] = useState<any>([]);

  const [gridHeight, setGridHeight] = useState(608);
  const [selectedRow, setSelectedRow] = useState<GridRowSelectionModel>([]);
  const [selectedPage, setSelectedPage] = useState<GridPaginationModel>({ pageSize: pageSize, page: 0 });

  const toNumber = (value: any) => {
    const numberValue = Number(String(value ?? 0).replace(/,/g, ""));
    return Number.isFinite(numberValue) ? numberValue : 0;
  };

  const formatNumber = (value: any) => {
    return cUtils.numericComma(toNumber(value)) || "0";
  };

  const renderNumberCell = (param: GridRenderCellParams) => {
    return <span>{formatNumber(param.value)}</span>;
  };

  const columns: GridColDef[] = [
    {
      field: "broad_start_tm",
      headerName: "시작일시",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (param: GridRenderCellParams) => {
        return (
          <>
            <span>{param.row.broad_start_tm !== "" ? dayjs(param.row.broad_start_tm).format("YYYY-MM-DD HH:mm") : ""}</span>
          </>
        );
      },
    },
    {
      field: "broad_status",
      headerName: "상태",
      width: 80,
      headerAlign: "center",
      align: "center",
      renderCell: (param: GridRenderCellParams) => {
        return (
          <>
            <span>{broadStatusName[param.row.broad_status]}</span>
          </>
        );
      },
    },
    {
      field: "broad_title",
      headerName: "방송명",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "total_views",
      headerName: "전체 시청자수",
      width: 160,
      headerAlign: "right",
      align: "right",
      renderCell: renderNumberCell,
    },
    {
      field: "live_total_view_cnt",
      headerName: "라이브 시청자수",
      width: 110,
      headerAlign: "right",
      align: "right",
      renderCell: renderNumberCell,
    },
    {
      field: "live_total_unique_view_cnt",
      headerName: "순수 시청자수",
      width: 130,
      headerAlign: "right",
      align: "right",
      renderCell: renderNumberCell,
    },
    {
      field: "live_avg_concurrent_view_cnt",
      headerName: "평균 동시시청자",
      width: 150,
      headerAlign: "right",
      align: "right",
      renderCell: renderNumberCell,
    },
    {
      field: "live_avg_view_time_secs",
      headerName: "평균 시청시간",
      width: 140,
      headerAlign: "right",
      align: "right",
      renderCell: (param: GridRenderCellParams) => {
        return (
          <>
            <span>{convertTotalViewTime(param.row.live_avg_view_time_secs)}</span>
          </>
        );
      },
    },
    {
      field: "live_total_prod_click",
      headerName: "상품 클릭 수",
      width: 120,
      headerAlign: "right",
      align: "right",
      renderCell: renderNumberCell,
    },
    {
      field: "chat_list",
      headerName: "채팅 다운로드",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            {params.row.broad_seq === "total_sum" ? (
              <></>
            ) : (
              <Button
                variant="contained"
                onClick={() => {
                  getListChannelMessages(params.row.broad_seq, params.row.broad_start_tm, params.row.broad_title);
                }}
              >
                다운로드
              </Button>
            )}
          </>
        );
      },
    },
    {
      field: "pk",
      headerName: "세부통계",
      width: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (param: GridRenderCellParams) => {
        return (
          <>
            {param.row.broad_seq === "total_sum" ? (
              <></>
            ) : (
              <Tooltip title="방송중 또는 방송종료 후 확인 가능">
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      navigate(`/statistic/${param.row.broad_seq}`);
                    }}
                    disabled={param.row.broad_status === "CREATE" || param.row.broad_status === "READY"}
                  >
                    자세히
                  </Button>
                </Box>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    get_channel_statistics_list();
    return () => {
      setIsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const get_channel_statistics_list = useCallback(async () => {
    if (props.userState.id === "" || props.userState.id === undefined) return;

    if (srchDateFrom === null || srchDateTo === null) {
      toastRef.current?.toast("조회 기간을 선택해 주세요.", "error", 3000, { vertical: "top", horizontal: "center" });
      return;
    }
    if (srchDateFrom.isAfter(srchDateTo)) {
      toastRef.current?.toast("조회 시작일이 종료일보다 늦을 수 없습니다.", "error", 3000, { vertical: "top", horizontal: "center" });
      return;
    }
    if (srchDateTo.diff(srchDateFrom, "day") > maxSearchDays) {
      toastRef.current?.toast("조회 기간은 최대 1년까지 가능합니다.", "error", 3000, { vertical: "top", horizontal: "center" });
      return;
    }

    setIsLoading(true);

    const param = {
      command: "get_channel_statistics_list",
      search_condition: {
        broad_date: [dayjs(srchDateFrom).format("YYYYMMDD"), dayjs(srchDateTo).add(1, "day").format("YYYYMMDD")],
        broad_status: ["CREATE", "START", "READY", "STOP", "VOD"],
        sort: "DESCEND",
      },
      request_user_id: props.userState.id,
    };

    const res = await channelApi.post(param);
    if (res.result_code === "200") {
      let tempList: any = [];
      let total_sum_unique_view_cnt = 0;
      let total_sum_live_view_cnt = 0;
      let total_sum_view_cnt = 0;
      let total_sum_prod_click_cnt = 0;
      let total_sum_delivered_secs = 0;
      let total_sum_concurrent_view_cnt = 0;
      let total_sum_statistic_count = 0;
      for (const body of res.result_body) {
        const liveTotalUniqueViewCnt = toNumber(body.live_total_unique_view_cnt);
        const liveTotalViewCnt = toNumber(body.live_total_view_cnt);
        const totalViewCnt = toNumber(body.total_views);
        const liveTotalProdClick = toNumber(body.live_total_prod_click);
        const liveTotalDeliveredSecs = toNumber(body.live_total_delivered_secs);
        const liveTotalConcurrentViewCnt = toNumber(body.live_total_concurrent_view_cnt);
        const liveStatisticCount = toNumber(body.live_statistic_count);

        total_sum_unique_view_cnt = total_sum_unique_view_cnt + liveTotalUniqueViewCnt;
        total_sum_live_view_cnt = total_sum_live_view_cnt + liveTotalViewCnt;
        total_sum_view_cnt = total_sum_view_cnt + totalViewCnt;
        total_sum_prod_click_cnt = total_sum_prod_click_cnt + liveTotalProdClick;
        total_sum_delivered_secs = total_sum_delivered_secs + liveTotalDeliveredSecs;
        total_sum_concurrent_view_cnt = total_sum_concurrent_view_cnt + liveTotalConcurrentViewCnt;
        total_sum_statistic_count = total_sum_statistic_count + liveStatisticCount;

        tempList.push({
          ...body,
          live_total_unique_view_cnt: liveTotalUniqueViewCnt,
          live_total_view_cnt: liveTotalViewCnt,
          total_views: totalViewCnt,
          live_total_prod_click: liveTotalProdClick,
          live_total_delivered_secs: liveTotalDeliveredSecs,
          live_total_concurrent_view_cnt: liveTotalConcurrentViewCnt,
          live_statistic_count: liveStatisticCount,
          live_avg_concurrent_view_cnt: toNumber(body.live_avg_concurrent_view_cnt),
          live_avg_view_time_secs: toNumber(body.live_avg_view_time_secs),
        });
      }

      tempList.unshift({
        broad_seq: "total_sum",
        broad_start_tm: "",
        host_id: "총계",
        broad_title: "총계",
        live_total_unique_view_cnt: total_sum_unique_view_cnt,
        live_total_view_cnt: total_sum_live_view_cnt,
        total_views: total_sum_view_cnt,
        live_total_prod_click: total_sum_prod_click_cnt,
        live_total_delivered_secs: total_sum_delivered_secs,
        live_total_concurrent_view_cnt: total_sum_concurrent_view_cnt,
        live_statistic_count: total_sum_statistic_count,
        live_avg_concurrent_view_cnt:
          total_sum_statistic_count > 0 ? Math.round(total_sum_concurrent_view_cnt / total_sum_statistic_count) : 0,
        live_avg_view_time_secs:
          total_sum_concurrent_view_cnt > 0 ? Math.round(total_sum_delivered_secs / total_sum_concurrent_view_cnt) : 0,
      });
      setChannelList(tempList);
    } else {
      console.error(res.result_body);
      setChannelList([]);
      const errorMsg = res.result_body?.[0]?.error_msg;
      const message = typeof errorMsg === "string" && errorMsg !== "" ? errorMsg : "통계 조회에 실패했습니다. 조회 기간을 줄여서 다시 시도해 주세요.";
      toastRef.current?.toast(message, "error", 3000, { vertical: "top", horizontal: "center" });
    }

    setIsLoading(false);
  }, [srchDateFrom, srchDateTo, props.userState.id]);

  // 채팅 다운로드: 방송의 전체 채팅을 분할 조회로 모두 받아 엑셀로 저장한다.
  // 한 번에 전부 읽으면 채팅이 많은 방송에서 Lambda 응답 한도(6MB)를 넘겨 실패하므로 페이지로 나눠 받는다.
  const CHAT_DOWNLOAD_PAGE_SIZE = 2000;
  const CHAT_DOWNLOAD_MAX_PAGES = 200; // 안전장치 (최대 40만 건)
  const getListChannelMessages = async (broad_seq: string, broad_start_tm: String, broad_title: String) => {
    setIsLoading(true);

    const allMessages: any[] = [];
    let lastKey: string = "";
    let failed = false;

    for (let page = 0; page < CHAT_DOWNLOAD_MAX_PAGES; page++) {
      const param: any = {
        command: "list_chat_history",
        broad_seq: broad_seq,
        next_token: undefined,
        list_mode: "total",
        limit: CHAT_DOWNLOAD_PAGE_SIZE,
        last_key: lastKey,
      };

      const result: any = await adminApi.post(param);
      if (result.code !== "200") {
        failed = true;
        break;
      }

      allMessages.push(...(result.response.chat_history.Messages || []));
      lastKey = result.response.last_key || "";
      if (!lastKey) break;
    }

    if (failed) {
      setIsLoading(false);
      toastRef.current?.toast("채팅 내역을 불러오지 못했습니다.", "error", 3000, { vertical: "top", horizontal: "center" });
      return;
    }

    {
      {
        // sheet header
        let chatHistory: any = [["순번", "채팅일시", "작성자", "내용"]];
        // 각 행 순번 만들기
        let chatIndex = 1;
        // 내용 생성
        for (const message of allMessages) {
          const detail = [
            chatIndex,
            // 채팅일시는 DDB 정렬 키에서 오는데 시청자 채팅은 "시각#난수" 형태라 시각 부분만 떼어 파싱한다.
            // 난수는 같은 마이크로초에 들어온 두 건이 서로를 덮어쓰지 않게 붙인 값이라 정렬 키에는 그대로 둔다.
            dayjs(String(message.CreatedTimestamp).split("#")[0]).format("YYYY-MM-DD HH:mm:ss"),
            `${message.Sender.Name}(${
              message.Sender.Arn.split("/user/").length > 1 ? message.Sender.Arn.split("/user/")[1] : message.Sender.Arn
            })`,
            message.Content,
          ];
          chatHistory = [...chatHistory, detail];
          chatIndex += 1;
        }
        // 컬럼 넓이
        const colWidth = [{ wpx: 40 }, { wpx: 150 }, { wpx: 230 }, { wpx: 800 }];
        const sheetName = "채팅내역";
        // 파일명
        const fileName = dayjs(String(broad_start_tm)).format("YYYY-MM-DD HHmm") + "_채팅_" + broad_title + ".xlsx";
        cUtils.downloadExcel(chatHistory, colWidth, sheetName, fileName);
      }
    }
    setIsLoading(false);
  };

  const downloadExcelList = () => {
    if (channelList.length === 0) {
      toastRef.current?.toast("다운로드할 데이터가 없습니다.", "error", 3000, { vertical: "top", horizontal: "center" });
      return;
    }
    // 컬럼 넓이
    const colWidth = [
      { wpx: 40 },
      { wpx: 150 },
      { wpx: 220 },
      { wpx: 110 },
      { wpx: 100 },
      { wpx: 110 },
      { wpx: 130 },
      { wpx: 120 },
      { wpx: 100 },
    ];
    // sheet header
    let statisticsHistory: any = [
      [
        "순번",
        "시작일시",
        "방송명",
        "전체 시청자수",
        "라이브 시청자수",
        "순수 시청자수",
        "평균 동시시청자",
        "평균 시청시간(초)",
        "상품 클릭 수",
      ],
    ];
    // 각 행 순번 만들기
    let statisticsIndex = 1;
    // 내용 생성
    for (const item of channelList) {
      let datetime = "";
      if (item.broad_start_tm !== "") {
        datetime = dayjs(item.broad_start_tm, "YYYYMMDDHHmmss").format("YYYY-MM-DD HH:mm");
      }
      const detail = [
        statisticsIndex,
        datetime,
        item.broad_title,
        toNumber(item.total_views),
        toNumber(item.live_total_view_cnt),
        toNumber(item.live_total_unique_view_cnt),
        toNumber(item.live_avg_concurrent_view_cnt),
        toNumber(item.live_avg_view_time_secs),
        toNumber(item.live_total_prod_click),
      ];
      statisticsHistory = [...statisticsHistory, detail];
      statisticsIndex += 1;
    }

    const statsticsSheetName = "통계내역";
    // 파일명
    const fileName = `[${dayjs(srchDateFrom).format("YYYY-MM-DD")}~${dayjs(srchDateTo).format(
      "YYYY-MM-DD"
    )}] 방송별통계.xlsx`;
    cUtils.downloadExcel(statisticsHistory, colWidth, statsticsSheetName, fileName);
  };

  const convertTotalViewTime = (totalSec: number) => {
    const t = cUtils.convertSecToTime(toNumber(totalSec));
    return `${t.hours}:${t.minutes}:${t.seconds}`;
  };

  function CustomPagination() {
    const page = useGridSelector(gridApi, gridPageSelector);
    const pageCount = useGridSelector(gridApi, gridPageCountSelector);
    return (
      <Box className="tenants-list-pagenation">
        <Pagination
          color="primary"
          shape="rounded"
          count={pageCount === 0 ? 1 : pageCount}
          page={page + 1}
          siblingCount={10}
          onChange={(e, v) => gridApi.current.setPage(v - 1)}
        ></Pagination>
      </Box>
    );
  }

  useEffect(() => {
    setGridHeight(windowSize.height - 220);
  }, [windowSize]);

  return (
    <>
      <Box sx={{ p: 0, height: "100%" }}>
        <Stack direction={"column"} spacing={0}>
          <Box className="content-header-root">
            <Typography variant="h5" className="content-header-title">
              방송별 통계
            </Typography>
          </Box>
          <Divider />
          <Box className="content-content-root">
            <Stack direction={"column"} spacing={1}>
              <Box className="content-content-header" sx={{ pb: 0 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Box>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker", "DatePicker"]}>
                        <DatePicker
                          label="방송시작 From"
                          showDaysOutsideCurrentMonth
                          format="YYYY-MM-DD"
                          value={srchDateFrom}
                          onChange={(newValue) => setSrchDateFrom(newValue)}
                          sx={{ width: "160px", minWidth: "160px !important" }}
                          slotProps={{
                            textField: { size: "small" },
                          }}
                        />
                        <DatePicker
                          label="방송시작 To"
                          showDaysOutsideCurrentMonth
                          format="YYYY-MM-DD"
                          value={srchDateTo}
                          onChange={(newValue) => setSrchDateTo(newValue)}
                          sx={{ width: "160px", minWidth: "160px !important" }}
                          slotProps={{
                            textField: { size: "small" },
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: "10px",
                    minWidth: "200px",
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button variant="contained" color="primary" onClick={get_channel_statistics_list}>
                    조회
                  </Button>
                  <Button variant="contained" size="small" onClick={downloadExcelList}>
                    EXCEL
                  </Button>
                </Box>
              </Box>
              {/* 라이브 리스트 */}
              <Box sx={{ width: "100%", height: gridHeight }}>
                <DataGrid
                  apiRef={gridApi}
                  rows={channelList}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: pageSize,
                      },
                    },
                    sorting: {
                      sortModel: [
                        {
                          field: "range_num",
                          sort: "asc",
                        },
                      ],
                    },
                  }}
                  slots={{ pagination: CustomPagination }}
                  hideFooterSelectedRowCount
                  getRowId={(row) => row.broad_seq}
                  rowSelectionModel={selectedRow}
                  onRowSelectionModelChange={(newRowSelectionModel) => {
                    setSelectedRow(newRowSelectionModel);
                  }}
                  paginationModel={selectedPage}
                  onPaginationModelChange={(newPagenationModel) => {
                    setSelectedPage(newPagenationModel);
                  }}
                  rowHeight={(gridHeight - 60 - 60 + 11) / 10}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
      <LoadingCircle loading={isLoading} />
      <Toast ref={toastRef} />
    </>
  );
};

export default TotalStatistic;
